import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import './StudentDashboard.css';
import StudentSidebar from './StudentSidebar';
import './StudentSidebar.css';
import HomeStudent from './HomeStudent';
import Dictionary from './Dictionary';
import Homework from './Homework';
import StudentLessonView from './StudentLessonView/StudentLessonView';
import StudentProfileSettings from './StudentProfileSettings';
import StudentMobileBottomNav from './StudentMobileBottomNav';
import StudentChatModal from '../StudentChatModal.jsx';
import { getStudentByInviteSlug, getStudentByUserId } from '../../utils/studentsService';
import { getHomework } from '../../utils/homeworkService';
import { getFileUrl } from '../../utils/storageService';
import Tour from '../Tour/Tour';
import TourTrigger from '../Tour/TourTrigger';
import { STUDENT_DASHBOARD_TOUR_STEPS } from '../Tour/tourSteps';

const STUDENT_TOUR_SEEN_KEY = 'novas_tour_student_dashboard_v1_seen';

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { studentSlug, lessonId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [profileInitialTab, setProfileInitialTab] = useState(null);
  const [newHomeworkCount, setNewHomeworkCount] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  // Student's own photo for the bottom nav's Profile tab, Instagram-
  // style. StudentProfileSettings.jsx's photo upload doesn't write to
  // Supabase at all — it stores a base64 data URL straight into
  // localStorage's novas_students_data[slug].photo (and fires
  // studentDataUpdated on save), so studentData.photo from the DB
  // fetch is never actually the current photo. Read from the same
  // place StudentProfileSettings itself reads from, and fall back to
  // studentData.photo only if that's empty.
  useEffect(() => {
    let cancelled = false;
    const resolveAvatar = () => {
      let photo = null;
      try {
        const raw = localStorage.getItem('novas_students_data');
        const slug = studentData?.slug || studentData?.originalSlug || studentData?.id;
        const all = raw ? JSON.parse(raw) : {};
        photo = (slug && all[slug]?.photo) || studentData?.photo || null;
      } catch {
        photo = studentData?.photo || null;
      }
      if (!photo) {
        setAvatarUrl(null);
      } else if (photo.startsWith('http') || photo.startsWith('data:')) {
        // Already directly usable — a signed URL or a base64 data URL.
        setAvatarUrl(photo);
      } else {
        getFileUrl(photo, 31536000).then((url) => {
          if (!cancelled) setAvatarUrl(url);
        }).catch(() => {});
      }
    };
    resolveAvatar();
    window.addEventListener('studentDataUpdated', resolveAvatar);
    return () => {
      cancelled = true;
      window.removeEventListener('studentDataUpdated', resolveAvatar);
    };
  }, [studentData?.photo, studentData?.slug, studentData?.originalSlug, studentData?.id]);

  // Auto-open the dashboard tour on first visit. We wait 800ms so the
  // HomeStudent banner + lessons table are rendered and targetable.
  useEffect(() => {
    try {
      if (!localStorage.getItem(STUDENT_TOUR_SEEN_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, []);
  // eslint-disable-next-line no-unused-vars
  const [activeLessonId, setActiveLessonId] = useState(null);

  useEffect(() => {
    const loadStudent = async () => {
      // Check if user is authenticated
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      
      if (!isAuthenticated || !currentUser) {
        navigate('/login');
        return;
      }
      
      // Если это студент, загружаем его данные через user_account_id
      if (currentUser.role === 'student') {
        try {
          const student = await getStudentByUserId(currentUser.id);
          console.log('StudentDashboard - loaded student by userId:', student);
          console.log('StudentDashboard - student.teacher:', student?.teacher);
          console.log('StudentDashboard - student.teacherName:', student?.teacherName);
          
          if (student) {
            // Проверяем, что студент заходит на свой dashboard
            if (studentSlug && student.invite_slug !== studentSlug) {
              const suffix = location.pathname.replace(`/student-dashboard/${studentSlug}`, '');
              navigate(`/student-dashboard/${student.invite_slug}${suffix}${location.search}`, { replace: true });
              return;
            }
            
            // Используем данные студента с информацией о преподавателе
            setStudentData({
              ...student,
              slug: student.invite_slug || studentSlug
            });
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading student by userId:', error);
          // Продолжаем загрузку по slug как fallback
        }
      }
      
      // Загружаем студента по slug (fallback или для неавторизованных)
      if (studentSlug) {
        try {
          const student = await getStudentByInviteSlug(studentSlug);
          console.log('StudentDashboard - loaded student by slug:', student);
          console.log('StudentDashboard - student.teacher:', student?.teacher);
          console.log('StudentDashboard - student.teacherName:', student?.teacherName);
          
          if (student) {
            setStudentData({
              ...student,
              slug: studentSlug
            });
            setIsLoading(false);
          } else {
            setError('Student not found');
            setIsLoading(false);
          }
        } catch (error) {
          console.error('Error loading student:', error);
          setError('Error loading student data');
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    
    loadStudent();
  }, [studentSlug, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for student data updates
  useEffect(() => {
    const handleStudentDataUpdate = async () => {
      console.log('Student data updated in StudentDashboard, reloading...');
      
      if (studentSlug) {
        try {
          const student = await getStudentByInviteSlug(studentSlug);
          if (student) {
            setStudentData({
              ...student,
              slug: studentSlug
            });
            console.log('Updated studentData:', student);
          }
        } catch (error) {
          console.error('Error reloading student data:', error);
        }
      }
    };

    window.addEventListener('studentDataUpdated', handleStudentDataUpdate);
    
    return () => {
      window.removeEventListener('studentDataUpdated', handleStudentDataUpdate);
    };
  }, [studentSlug]);

  const fetchNewHomeworkCount = useCallback(async () => {
    const studentId = studentData?.id || studentData?.studentId;
    if (!studentId) return;
    try {
      const list = await getHomework(studentId);
      const count = (list || []).filter(h => h.status === 'assigned').length;
      setNewHomeworkCount(count);
    } catch (e) {
      setNewHomeworkCount(0);
    }
  }, [studentData?.id, studentData?.studentId]);

  useEffect(() => {
    if (!studentData?.id && !studentData?.studentId) return;
    fetchNewHomeworkCount();
  }, [studentData?.id, studentData?.studentId, fetchNewHomeworkCount]);

  useEffect(() => {
    window.addEventListener('homeworkUpdated', fetchNewHomeworkCount);
    return () => window.removeEventListener('homeworkUpdated', fetchNewHomeworkCount);
  }, [fetchNewHomeworkCount]);

  // Определяем активный раздел на основе URL
  useEffect(() => {
    const pathname = location.pathname;
    const sectionParam = searchParams.get('section');
    
    if (lessonId) {
      setActiveLessonId(lessonId);
      setActiveSection('lesson');
    } else if (sectionParam) {
      // Если есть параметр section в URL, используем его
      setActiveSection(sectionParam);
    } else if (pathname.includes('/homework')) {
      setActiveSection('homework');
    } else if (pathname.includes('/dictionary')) {
      setActiveSection('dictionary');
    } else if (pathname.includes('/progress')) {
      setActiveSection('progress');
    } else if (pathname.includes('/profile')) {
      setActiveSection('profile');
    } else {
      setActiveSection('home');
    }
  }, [lessonId, location.pathname, searchParams]);

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return <StudentProfileSettings studentData={studentData} initialTab={profileInitialTab} />;
      case 'home':
        return (
          <HomeStudent
            studentData={studentData}
            onLessonClick={handleLessonClick}
            onGoToHomework={() => setActiveSection('homework')}
            onGoToDictionary={() => setActiveSection('dictionary')}
          />
        );
      case 'lesson':
        return <StudentLessonView lessonId={lessonId} studentData={studentData} />;
      case 'dictionary':
        return <Dictionary studentData={studentData} />;
      case 'homework':
        return <Homework studentData={studentData} />;
      case 'progress':
        return <div className="progress-section"><h2>Progress Section</h2><p>Progress content will be here</p></div>;
      default:
        return (
          <HomeStudent
            studentData={studentData}
            onLessonClick={handleLessonClick}
            onGoToHomework={() => setActiveSection('homework')}
            onGoToDictionary={() => setActiveSection('dictionary')}
          />
        );
    }
  };

  const handleLessonClick = (lessonId) => {
    console.log('=== handleLessonClick DEBUG ===');
    console.log('lessonId received:', lessonId);
    
    // Переходим на URL с lessonId для StudentLessonView
    navigate(`/student-dashboard/${studentData.slug}/lesson/${lessonId}`);
  };

  if (isLoading) {
    return (
      <div className="student-dashboard loading">
        <div className="student-loading-spinner">
          <div className="student-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-dashboard error">
        <div className="student-error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="student-back-btn" onClick={() => navigate('/')}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleSectionChange = (section, tab = null) => {
    setProfileInitialTab(tab);
    setActiveSection(section);
  };

  return (
    <div className="student-dashboard">
      <StudentSidebar onSectionChange={handleSectionChange} activeSection={activeSection} studentData={studentData} newHomeworkCount={newHomeworkCount} />
      <div className="student-dashboard-main">
        {renderContent()}
      </div>
      <StudentMobileBottomNav
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onChatClick={() => setChatOpen(true)}
        newHomeworkCount={newHomeworkCount}
        avatarUrl={avatarUrl}
      />

      {/* Onboarding tour — only on Home section. Other sections (lesson,
          dictionary, homework, profile) keep the UI clean. The TOUR
          component itself stays mounted across sections so an active tour
          continues if the student clicks a sidebar item mid-tour. */}
      {activeSection === 'home' && (
        <TourTrigger
          onClick={() => setTourOpen(true)}
          pulsing={!tourOpen && (() => { try { return !localStorage.getItem(STUDENT_TOUR_SEEN_KEY); } catch (_) { return false; } })()}
          label="Tour"
        />
      )}
      <Tour
        steps={STUDENT_DASHBOARD_TOUR_STEPS}
        isOpen={tourOpen}
        onClose={() => {
          setTourOpen(false);
          try { localStorage.setItem(STUDENT_TOUR_SEEN_KEY, '1'); } catch (_) {}
        }}
        onComplete={() => {
          try { localStorage.setItem(STUDENT_TOUR_SEEN_KEY, '1'); } catch (_) {}
        }}
      />

      {/* Chat with teacher — floating button in the bottom-right corner.
          Reachable from every section (Home, Homework, Dictionary,
          Profile) so the student can message without needing the
          teacher to open a lesson first. Hidden inside lesson view
          because the lesson page has its own embedded chat panel. */}
      {studentData?.id && activeSection !== 'lesson' && (
        <>
          <button
            type="button"
            className="student-chat-fab"
            onClick={() => setChatOpen(true)}
            aria-label="Chat with your teacher"
            title="Chat with your teacher"
            /* Inline style as a safety-net — guarantees the FAB sits
             * above the mobile bottom-nav even if a CSS rule racing
             * over the class doesn't apply yet (HMR, late stylesheet
             * load, browser quirks with fixed-inside-flex-overflow). */
            style={{
              position: 'fixed',
              right: 'max(14px, env(safe-area-inset-right))',
              bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
              zIndex: 1500,
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
              <path d="M16 8c0 3.866-3.582 7-8 7a9 9 0 0 1-2.347-.306c-.584.296-1.925.864-4.181 1.234-.2.032-.352-.176-.273-.362.354-.836.674-1.95.77-2.966C.744 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7M5 8a1 1 0 1 0-2 0 1 1 0 0 0 2 0m4 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0m3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2"/>
            </svg>
          </button>
          <StudentChatModal
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            studentId={studentData.slug}
            studentName={studentData.name || ''}
            senderName={studentData.name || ''}
            isStudent={true}
          />
        </>
      )}
    </div>
  );
}
